import React, { useEffect, useState } from 'react';
import type { Influencer as InfluencerType } from '~/features/Api/apiSlice.type';
import { AppStoreUrls } from '~/types';
import chooseInfluencerState, {
  InfluencerState,
} from '~/features/Influencer/chooseInfluencerState';
import { InfluencerSmsSignup } from '~/features/Influencer/InfluencerSmsSignup';
import { InfluencerHero } from '~/features/Influencer/InfluencerHero';
import useWindowSize from '~/hooks/useWindowSize';
import { DidYouMiss } from '~/features/Influencer/DidYouMiss';
import { CantWatchNow } from '~/features/Influencer/CantWatchNow';
import { RegistrationSuccess } from '~/features/Influencer/RegistrationSuccess';
import SimpleModal from '~/components/ui/SimpleModal';
import Button from '~/components/ui/Button';

export function multiplyNumRegistered(count: number) {
  const SIGN_UP_CNT_MULTIPLIER = 4.2;

  return Math.round(count * SIGN_UP_CNT_MULTIPLIER);
}

interface InfluencerProps {
  influencer: InfluencerType;
  isInfluencerLive: boolean;
  latestLiveTime?: number;
  isStreamChosen: boolean;
  isVideoLive: boolean;
  isGuest: boolean;
  showRegistrationConfirmation: boolean;
  shopName: string;
  appStoreUrls: AppStoreUrls;
  liveSaleJustEnded: boolean;
  itemsInCart?: number;
}

const Influencer: React.FC<InfluencerProps> = ({
  influencer,
  isInfluencerLive,
  latestLiveTime,
  children,
  isStreamChosen,
  isVideoLive,
  isGuest,
  showRegistrationConfirmation,
  shopName,
  appStoreUrls,
  liveSaleJustEnded,
  itemsInCart = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(liveSaleJustEnded);
  const [influencerState, setInfluencerState] = useState<InfluencerState>(
    InfluencerState.Unknown,
  );

  const { width } = useWindowSize();
  const isSmallWidth = width < 576;

  useEffect(() => {
    if (itemsInCart) {
      setIsModalOpen(liveSaleJustEnded);
    }
  }, [liveSaleJustEnded, itemsInCart]);

  useEffect(() => {
    if (liveSaleJustEnded) {
      setInfluencerState(InfluencerState.After);
      return;
    }

    setInfluencerState(
      chooseInfluencerState({
        nextLiveTime: influencer.nextLive
          ? new Date(influencer.nextLive).getTime()
          : null,
        nowTime: Date.now(),
        isInfluencerLive,
        latestLiveTime,
      }),
    );
  }, [influencer, isInfluencerLive, latestLiveTime, liveSaleJustEnded]);

  const { name, imageUrl, nextLive } = influencer;

  const registrationTitle = `Live Sale with ${name}`;
  const registrationText = `Check out this live event with ${name} and ${shopName}`;
  const registrationUrl = window.location.pathname;
  const multipliedNumRegistered = multiplyNumRegistered(influencer.signUpCnt);

  return (
    <>
      <SimpleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className={'bg-white p-7 rounded text-center'}>
          <h1 className={'text-4xl'}>Thanks for watching!</h1>

          <p className={'mt-5 text-lg'}>
            You have {itemsInCart} item{itemsInCart !== 1 && 's'} in your cart.
          </p>

          <div className={'mt-8'}>
            <Button
              attrs={{ to: '/cart' }}
              button={{ text: 'Click here to checkout' }}
            />
          </div>
        </div>
      </SimpleModal>

      <InfluencerHero
        src={imageUrl}
        influencerName={name}
        nextLive={nextLive}
        hasEnded={influencerState === InfluencerState.After}
        isStreamChosen={isStreamChosen}
        videoIsLive={isVideoLive}
      >
        {!isSmallWidth &&
          influencerState === InfluencerState.After &&
          !showRegistrationConfirmation && <DidYouMiss />}

        {showRegistrationConfirmation ? (
          <RegistrationSuccess
            title={registrationTitle}
            text={registrationText}
            url={registrationUrl}
            appStoreUrls={appStoreUrls}
          />
        ) : (
          <InfluencerSmsSignup
            influencerSlug={influencer.slug}
            numRegistered={multipliedNumRegistered}
            isStreamChosen={isStreamChosen}
            isGuest={isGuest}
          />
        )}
      </InfluencerHero>

      <div>{children}</div>

      <div className={'max-w-screen-lg mx-auto mt-6 md:flex items-start'}>
        {influencerState === InfluencerState.During &&
          !showRegistrationConfirmation && <CantWatchNow />}

        {isSmallWidth &&
          influencerState === InfluencerState.After &&
          !showRegistrationConfirmation && (
            <div className={'m-4'}>
              <DidYouMiss />
            </div>
          )}

        {/* This displays all the time for small screens
            and only if video is playing on desktop. */}
        {(isSmallWidth || isStreamChosen) && (
          <div className={'flex-1 px-4'}>
            {showRegistrationConfirmation ? (
              <RegistrationSuccess
                title={registrationTitle}
                text={registrationText}
                url={registrationUrl}
                appStoreUrls={appStoreUrls}
              />
            ) : (
              <InfluencerSmsSignup
                influencerSlug={influencer.slug}
                numRegistered={influencer.signUpCnt}
                isStreamChosen={isStreamChosen}
                isGuest={isGuest}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Influencer;
